/**
 * reader-style-preset (= 副次 spec) の SettingsPanel modal。
 *
 * - 3 radio button group: fontSize / theme / pacing
 * - 設定変更 → useReaderStylePresetContext.updatePreset 即時呼出 (= localStorage 自動保存)
 * - 「閉じる」 button で onClose 呼出
 * - role="dialog" aria-modal="true" (= a11y)
 * - 既存 PlayerScreen の BackscrollView modal pattern を踏襲した overlay
 *
 * 設計詳細は `.kiro/specs/reader-style-preset/design.md` を参照。
 */

import { useRef, type JSX } from "react";
import { useDialogKeyboard } from "../../components/ui/useDialogKeyboard";
import { useReaderStylePresetContext } from "./ReaderStylePresetProvider";
import type {
  FlaggedContentVisibility,
  ReaderFontSize,
  ReaderPacing,
  ReaderTheme,
} from "./types";

interface SettingsPanelProps {
  onClose: () => void;
}

const FONT_SIZE_OPTIONS: ReadonlyArray<{
  value: ReaderFontSize;
  label: string;
}> = [
  { value: "small", label: "小" },
  { value: "medium", label: "標準" },
  { value: "large", label: "大" },
];

const THEME_OPTIONS: ReadonlyArray<{ value: ReaderTheme; label: string }> = [
  { value: "light", label: "明るい" },
  { value: "dark", label: "暗い" },
];

const PACING_OPTIONS: ReadonlyArray<{
  value: ReaderPacing;
  label: string;
}> = [
  { value: "slow", label: "遅" },
  { value: "normal", label: "標準" },
  { value: "fast", label: "速" },
];

const FLAGGED_OPTIONS: ReadonlyArray<{
  value: FlaggedContentVisibility;
  label: string;
}> = [
  { value: "hide", label: "非表示" },
  { value: "show", label: "表示する" },
];

// scene-beat-preview-toggle Phase 2: 物語構造 (= CurrentBeatIndicator) 表示 toggle の 2 option。
// 既存 FLAGGED_OPTIONS / fontSize の 2-3 option radio pattern と同 doctrine。
const BEAT_STRUCTURE_OPTIONS: ReadonlyArray<{
  value: "off" | "on";
  boolValue: boolean;
  label: string;
}> = [
  { value: "off", boolValue: false, label: "非表示" },
  { value: "on", boolValue: true, label: "表示する" },
];

export function SettingsPanel({ onClose }: SettingsPanelProps): JSX.Element {
  const { preset, updatePreset } = useReaderStylePresetContext();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // dialog 最低要件 (= Escape close + focus trap + initial focus) を hook に委譲。
  useDialogKeyboard({ containerRef: dialogRef, onClose });

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reader-style-settings-title"
      data-testid="reader-style-settings"
      className="fixed inset-0 z-50 flex items-start justify-center bg-reader-overlay overflow-y-auto p-4"
    >
      <div className="relative mt-8 w-full max-w-md rounded-lg bg-reader-page shadow-xl">
        <div className="flex items-center justify-between border-b border-reader-border p-4">
          <h2
            id="reader-style-settings-title"
            className="text-lg font-semibold text-reader-primary"
          >
            読書設定
          </h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center text-xl leading-none text-reader-secondary hover:text-reader-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6 p-4">
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-reader-secondary">
              文字サイズ
            </legend>
            <div className="flex gap-2">
              {FONT_SIZE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 cursor-pointer rounded border min-h-11 px-3 py-2 text-center text-sm focus-within:ring-2 focus-within:ring-reader-focus-ring inline-flex items-center justify-center ${
                    preset.fontSize === opt.value
                      ? "border-reader-brand bg-reader-brand/20 text-reader-primary"
                      : "border-reader-border bg-reader-surface text-reader-secondary hover:border-reader-border-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="reader-style-font-size"
                    value={opt.value}
                    checked={preset.fontSize === opt.value}
                    onChange={() => {
                      updatePreset({ fontSize: opt.value });
                    }}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-reader-secondary">
              テーマ
            </legend>
            <div className="flex gap-2">
              {THEME_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 cursor-pointer rounded border min-h-11 px-3 py-2 text-center text-sm focus-within:ring-2 focus-within:ring-reader-focus-ring inline-flex items-center justify-center ${
                    preset.theme === opt.value
                      ? "border-reader-brand bg-reader-brand/20 text-reader-primary"
                      : "border-reader-border bg-reader-surface text-reader-secondary hover:border-reader-border-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="reader-style-theme"
                    value={opt.value}
                    checked={preset.theme === opt.value}
                    onChange={() => {
                      updatePreset({ theme: opt.value });
                    }}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-reader-secondary">
              読み進めの速さ
            </legend>
            <div className="flex gap-2">
              {PACING_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 cursor-pointer rounded border min-h-11 px-3 py-2 text-center text-sm focus-within:ring-2 focus-within:ring-reader-focus-ring inline-flex items-center justify-center ${
                    preset.pacing === opt.value
                      ? "border-reader-brand bg-reader-brand/20 text-reader-primary"
                      : "border-reader-border bg-reader-surface text-reader-secondary hover:border-reader-border-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="reader-style-pacing"
                    value={opt.value}
                    checked={preset.pacing === opt.value}
                    onChange={() => {
                      updatePreset({ pacing: opt.value });
                    }}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-reader-secondary">
              物語構造の表示
            </legend>
            <p className="mb-2 text-xs text-reader-secondary">
              現在のシーンが物語のどの段階 (= 起 / 承 / 転 / 結) かを示すインジケータをシーン上部に表示します。
            </p>
            <div className="flex gap-2">
              {BEAT_STRUCTURE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 cursor-pointer rounded border min-h-11 px-3 py-2 text-center text-sm focus-within:ring-2 focus-within:ring-reader-focus-ring inline-flex items-center justify-center ${
                    preset.showBeatStructure === opt.boolValue
                      ? "border-reader-brand bg-reader-brand/20 text-reader-primary"
                      : "border-reader-border bg-reader-surface text-reader-secondary hover:border-reader-border-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="reader-style-show-beat-structure"
                    value={opt.value}
                    checked={preset.showBeatStructure === opt.boolValue}
                    onChange={() => {
                      updatePreset({ showBeatStructure: opt.boolValue });
                    }}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-reader-secondary">
              注意喚起されたシーンの表示
            </legend>
            <p className="mb-2 text-xs text-reader-muted">
              コミュニティガイドラインに基づき注意喚起されたシーンの扱いを選択します。
            </p>
            <div className="flex gap-2">
              {FLAGGED_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 cursor-pointer rounded border px-3 py-2 text-center text-sm focus-within:ring-2 focus-within:ring-reader-focus-ring ${
                    preset.flaggedContentVisibility === opt.value
                      ? "border-reader-brand bg-reader-brand/20 text-reader-primary"
                      : "border-reader-border bg-reader-surface text-reader-secondary hover:border-reader-border-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="reader-style-flagged"
                    value={opt.value}
                    checked={preset.flaggedContentVisibility === opt.value}
                    onChange={() => {
                      // 18+ 確認 (= 'show' に切替時のみ、 'hide' へは確認なし)
                      if (
                        opt.value === "show" &&
                        typeof window !== "undefined"
                      ) {
                        const ok = window.confirm(
                          "注意喚起されたシーンを表示します。 18 歳以上ですか?",
                        );
                        if (!ok) return;
                      }
                      updatePreset({ flaggedContentVisibility: opt.value });
                    }}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="flex justify-end border-t border-reader-border p-4">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded bg-reader-brand px-4 py-2 text-sm font-medium text-white hover:bg-reader-brand-hover"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
